import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, X, Pencil, Trash2, ChevronRight } from 'lucide-react'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'
import { pedirExclusao } from '@/lib/confirmarExclusao'
import { enterAdvance } from '@/hooks/useEnterAdvance'

const tipos: Record<string, string> = {
  receita: 'Receita', deducao: 'Deducao', custo: 'Custo',
  despesa_op: 'Desp.Op.', despesa_fin: 'Desp.Fin.', receita_fin: 'Rec.Fin.',
}
const cores: Record<string, string> = {
  receita: 'text-emerald-600', deducao: 'text-amber-600', custo: 'text-red-600',
  despesa_op: 'text-red-600', despesa_fin: 'text-red-600', receita_fin: 'text-emerald-600',
}
const EMPTY = { id: null as number | null, codigo: '', nome: '', tipo: 'despesa_op', pai_id: '', sintetica: false, descricao: '' }

export default function PlanoContasPage() {
  const qc = useQueryClient()
  const [show, setShow] = useState(false)
  const [form, setForm] = useState<any>(EMPTY)

  const { data: contas = [] } = useQuery<any[]>({
    queryKey: ['plano-contas'],
    queryFn: async () => (await api.get('/plano-contas')).data,
  })

  const salvar = useMutation({
    mutationFn: async () => {
      const payload = { ...form, pai_id: form.pai_id ? Number(form.pai_id) : null }
      if (form.id) return (await api.put(`/plano-contas/${form.id}`, payload)).data
      return (await api.post('/plano-contas', payload)).data
    },
    onSuccess: () => {
      toast.success(form.id ? 'Conta atualizada' : 'Conta criada')
      setShow(false); setForm(EMPTY)
      qc.invalidateQueries({ queryKey: ['plano-contas'] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao salvar'),
  })

  const excluir = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/plano-contas/${id}`)).data,
    onSuccess: () => { toast.success('Conta excluída'); qc.invalidateQueries({ queryKey: ['plano-contas'] }) },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao excluir'),
  })

  function abrirNova() { setForm(EMPTY); setShow(true) }
  function abrirEditar(c: any) {
    setForm({ id: c.id, codigo: c.codigo, nome: c.nome, tipo: c.tipo, pai_id: c.pai_id ? String(c.pai_id) : '', sintetica: c.sintetica, descricao: c.descricao || '' })
    setShow(true)
  }

  return (
    <div className="space-y-4 max-w-4xl">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">Plano de Contas Gerencial</h3>
        <button onClick={abrirNova} className="btn-primary"><Plus size={16} /> Nova</button>
      </div>
      <div className="card">
        <div className="divide-y">
          {contas.map((c: any) => (
            <div key={c.id}
              className={`px-4 py-2.5 flex items-center gap-2 text-sm group ${c.sintetica ? 'bg-ink-50 font-semibold' : 'hover:bg-ink-50'}`}
              style={{ paddingLeft: (c.nivel * 20) + 16 }}>
              {c.sintetica && <ChevronRight size={14} className="text-ink-400" />}
              <span className="font-mono text-xs text-ink-400 w-12">{c.codigo}</span>
              <span className="flex-1">{c.nome}</span>
              <span className={`text-xs font-medium ${cores[c.tipo] || ''}`}>{tipos[c.tipo] || c.tipo}</span>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
                <button onClick={() => abrirEditar(c)} className="p-1 rounded hover:bg-sky-100 text-sky-600" title="Editar"><Pencil size={13} /></button>
                <button onClick={() => pedirExclusao({ descricao: `a conta "${c.codigo} - ${c.nome}"`, onConfirm: () => excluir.mutate(c.id) })}
                  disabled={excluir.isPending} className="p-1 rounded hover:bg-rose-100 text-rose-600 disabled:opacity-40" title="Excluir"><Trash2 size={13} /></button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {show && (
        <div className="fixed inset-0 bg-ink-950/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md" onKeyDown={enterAdvance}>
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <h3 className="font-semibold">{form.id ? 'Editar Conta' : 'Nova Conta'}</h3>
              <button onClick={() => setShow(false)}><X size={18} /></button>
            </div>
            <div className="p-5 space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div><label className="label">Codigo</label>
                  <input className="input" value={form.codigo} onChange={e => setForm((f: any) => ({ ...f, codigo: e.target.value }))} /></div>
                <div className="col-span-2"><label className="label">Nome</label>
                  <input className="input" value={form.nome} onChange={e => setForm((f: any) => ({ ...f, nome: e.target.value }))} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="label">Tipo</label>
                  <select className="input" value={form.tipo} onChange={e => setForm((f: any) => ({ ...f, tipo: e.target.value }))}>
                    {Object.entries(tipos).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select></div>
                <div><label className="label">Pai</label>
                  <select className="input" value={form.pai_id} onChange={e => setForm((f: any) => ({ ...f, pai_id: e.target.value }))}>
                    <option value="">Raiz</option>
                    {contas.filter((c: any) => c.id !== form.id).map((c: any) =>
                      <option key={c.id} value={c.id}>{c.codigo} - {c.nome}{c.sintetica ? '' : ' (vira grupo)'}</option>)}
                  </select></div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.sintetica} onChange={e => setForm((f: any) => ({ ...f, sintetica: e.target.checked }))} />
                Sintetica (agrupadora)
              </label>
            </div>
            <div className="px-5 py-4 border-t flex gap-2">
              <button onClick={() => setShow(false)} className="btn-secondary flex-1">Cancelar</button>
              <button tabIndex={0} onClick={() => salvar.mutate()} disabled={!form.codigo || !form.nome || salvar.isPending} className="btn-primary flex-1">
                {form.id ? 'Salvar' : 'Criar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
